import { sortByName } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ClockIcon, CheckCircleIcon, XCircleIcon, PlusIcon,
  XMarkIcon, FunnelIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .lm-root * { box-sizing:border-box; }
  .lm-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:lmFade .4s ease both; }
  @keyframes lmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .lm-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .lm-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .lm-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover   { background:#ef4444; color:white; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm    { padding:6px 12px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }

  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; }
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.green{background:#ecfdf5;color:#10c98e}
  .s-icon.red{background:#fef2f2;color:#ef4444}   .s-icon.blue{background:#eef2ff;color:#3b6ef8}
  .s-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .s-value { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.1; }

  .filters { display:flex; gap:10px; margin-bottom:1.2rem; flex-wrap:wrap; align-items:center; }
  .filter-select,.filter-input { font-family:'Sora',sans-serif; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; color:#1a1d2e; outline:none; }
  .filter-select:focus,.filter-input:focus { border-color:#3b6ef8; }

  .table-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }

  /* Desktop table */
  .leave-table { width:100%; border-collapse:collapse; }
  .leave-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .leave-table th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .leave-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .leave-table tbody tr:last-child { border-bottom:none; }
  .leave-table tbody tr:hover { background:#fafbff; }
  .leave-table td { padding:11px 16px; vertical-align:middle; font-size:.8rem; text-align:center !important; }

  /* Mobile leave cards */
  .leave-cards { display:none; padding:10px; }
  .leave-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .lc-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .lc-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .lc-row:last-of-type { border-bottom:none; }
  .lc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .lc-actions { display:flex; gap:6px; margin-top:8px; }

  .badge { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; white-space:nowrap; }
  .badge svg { width:11px; height:11px; stroke-width:2.5; }
  .badge.pending         { background:#fffbeb; color:#f59e0b; }
  .badge.approved-paid   { background:#ecfdf5; color:#10c98e; }
  .badge.approved-unpaid { background:#eef2ff; color:#3b6ef8; }
  .badge.rejected        { background:#fef2f2; color:#ef4444; }
  .badge.sick            { background:#fdf4ff; color:#a855f7; }
  .badge.casual          { background:#eff6ff; color:#3b82f6; }
  .badge.emergency       { background:#fff7ed; color:#f97316; }
  .badge.maternity       { background:#fdf2f8; color:#ec4899; }
  .badge.unpaid          { background:#f9fafb; color:#6b7280; }

  .teacher-cell { display:flex; align-items:center; gap:9px; }
  .t-avatar { width:30px; height:30px; border-radius:8px; background:#eef2ff; color:#3b6ef8; display:flex; align-items:center; justify-content:center; font-size:.7rem; font-weight:700; flex-shrink:0; }
  .t-name  { font-size:.8rem; font-weight:600; color:#0f1123; }
  .t-email { font-size:.68rem; color:#9094a8; }

  .date-range { font-family:'JetBrains Mono',monospace; font-size:.74rem; color:#1a1d2e; }
  .date-days  { font-size:.67rem; color:#9094a8; margin-top:1px; }
  .reason-cell { max-width:180px; font-size:.76rem; color:#6b7194; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .action-btns { display:flex; gap:6px; }

  .lm-empty { text-align:center; padding:3.5rem 2rem; }
  .lm-empty-icon { width:48px; height:48px; border-radius:13px; background:#fffbeb; color:#f59e0b; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .lm-empty-icon svg { width:22px; height:22px; stroke-width:1.6; }
  .lm-empty-title { font-size:.9rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .lm-empty-sub   { font-size:.76rem; color:#9094a8; }

  .lm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:500px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-close svg { width:15px; height:15px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:20px; flex-wrap:wrap; }

  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.span2 { grid-column:1/-1; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; transition:border-color .15s; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#3b6ef8; background:white; }
  .form-textarea { resize:vertical; min-height:70px; }

  .review-info { background:#f8f9fc; border-radius:12px; padding:14px 16px; margin-bottom:16px; }
  .review-row  { display:flex; justify-content:space-between; padding:4px 0; border-bottom:1px solid #f0f2f8; font-size:.78rem; gap:8px; }
  .review-row:last-child { border-bottom:none; }
  .review-key  { color:#6b7194; flex-shrink:0; }
  .review-val  { font-weight:600; color:#1a1d2e; text-align:right; }
  .review-options { display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }
  .review-option  { display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px; border:1.5px solid #e0e4f0; cursor:pointer; transition:all .15s; font-size:.82rem; font-weight:500; }
  .review-option:hover   { border-color:#3b6ef8; background:#fafbff; }
  .review-option.selected-green  { border-color:#10c98e; background:#ecfdf5; color:#065f46; }
  .review-option.selected-blue   { border-color:#3b6ef8; background:#eef2ff; color:#3730a3; }
  .review-option.selected-red    { border-color:#ef4444; background:#fef2f2; color:#991b1b; }
  .review-option-dot { width:14px; height:14px; border-radius:50%; border:2px solid currentColor; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
  .review-option-dot.filled::after { content:''; width:6px; height:6px; border-radius:50%; background:currentColor; display:block; }

  /* ── Mobile ── */
  @media (max-width:900px) { .stat-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:768px) {
    .lm-title { font-size:1.1rem; }
    .lm-header { margin-bottom:1rem; }

    /* Stack filters */
    .filters { flex-direction:column; align-items:stretch; gap:8px; }
    .filter-select,.filter-input { width:100%; }
    .filters .btn { width:100%; justify-content:center; }

    /* Hide table, show cards */
    .leave-table-wrap { display:none !important; }
    .leave-cards { display:block; }

    /* Modals */
    .modal { padding:18px; }
    .form-grid { grid-template-columns:1fr; }
    .form-field.span2 { grid-column:1; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }
  @media (max-width:480px) { .stat-grid { grid-template-columns:1fr 1fr; } }
`;

const initials  = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
const fmtDate   = (d) => new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'});
const dayCount  = (s,e) => Math.round((new Date(e)-new Date(s))/86400000)+1;
const leaveTypeLabel = { sick:'Sick', casual:'Casual', emergency:'Emergency', maternity:'Maternity', unpaid:'Unpaid' };
const statusLabel    = { pending:'Pending', 'approved-paid':'Approved (Paid)', 'approved-unpaid':'Approved (Unpaid)', rejected:'Rejected' };

const LeaveManagement = () => {
  const [leaves,        setLeaves]        = useState([]);
  const [teachers,      setTeachers]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterStatus,  setFilterStatus]  = useState('');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterMonth,   setFilterMonth]   = useState('');
  const [applyOpen,     setApplyOpen]     = useState(false);
  const [reviewLeave,   setReviewLeave]   = useState(null);
  const [reviewChoice,  setReviewChoice]  = useState('');
  const [adminNote,     setAdminNote]     = useState('');
  const [submitting,    setSubmitting]    = useState(false);
  const [applyForm,     setApplyForm]     = useState({ teacherId:'', startDate:'', endDate:'', type:'sick', reason:'' });

  useEffect(() => { fetchTeachers(); }, []);
  useEffect(() => { fetchLeaves(); }, [filterStatus,filterTeacher,filterMonth]);

  const fetchTeachers = async () => {
    try { const {data}=await api.get('/admin/salary/teachers'); setTeachers(data); }
    catch { toast.error('Failed to load teachers'); }
  };

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const params=new URLSearchParams();
      if (filterStatus)  params.append('status',filterStatus);
      if (filterTeacher) params.append('teacherId',filterTeacher);
      if (filterMonth)   params.append('month',filterMonth);
      const {data}=await api.get(`/admin/leaves?${params}`);
      setLeaves(data);
    } catch { toast.error('Failed to load leaves'); }
    finally { setLoading(false); }
  };

  const handleApply = async () => {
    if (!applyForm.teacherId||!applyForm.startDate||!applyForm.endDate) { toast.error('Teacher, start and end date required'); return; }
    if (!applyForm.reason.trim()) { toast.error('Reason is required'); return; }
    try {
      setSubmitting(true);
      await api.post('/admin/leaves',applyForm);
      toast.success('Leave application created');
      setApplyOpen(false);
      setApplyForm({teacherId:'',startDate:'',endDate:'',type:'sick',reason:''});
      fetchLeaves();
    } catch(err) { toast.error(err.response?.data?.message||'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleReview = async () => {
    if (!reviewChoice) { toast.error('Please select an action'); return; }
    try {
      setSubmitting(true);
      await api.put(`/admin/leaves/${reviewLeave._id}/review`,{status:reviewChoice,adminNote});
      toast.success('Leave updated');
      setReviewLeave(null); setReviewChoice(''); setAdminNote('');
      fetchLeaves();
    } catch(err) { toast.error(err.response?.data?.message||'Failed'); }
    finally { setSubmitting(false); }
  };

  const pending        = leaves.filter(l=>l.status==='pending').length;
  const approvedPaid   = leaves.filter(l=>l.status==='approved-paid').length;
  const approvedUnpaid = leaves.filter(l=>l.status==='approved-unpaid').length;
  const rejected       = leaves.filter(l=>l.status==='rejected').length;

  const openReview = (leave) => { setReviewLeave(leave); setReviewChoice(leave.status==='pending'?'':leave.status); setAdminNote(leave.adminNote||''); };

  const renderCards = () => (
    <div className="leave-cards">
      {leaves.map(leave => (
        <div key={leave._id} className="leave-card">
          <div className="lc-top">
            <div className="teacher-cell">
              <div className="t-avatar">{initials(leave.teacher?.name)}</div>
              <div>
                <div className="t-name">{leave.teacher?.name||'—'}</div>
                <div className="t-email">{leave.teacher?.email||''}</div>
              </div>
            </div>
            <span className={`badge ${leave.status}`}>{statusLabel[leave.status]}</span>
          </div>
          <div className="lc-row">
            <span className="lc-label">Type</span>
            <span className={`badge ${leave.type}`}>{leaveTypeLabel[leave.type]||leave.type}</span>
          </div>
          <div className="lc-row">
            <span className="lc-label">Dates</span>
            <div style={{textAlign:'right'}}>
              <div className="date-range" style={{fontSize:'.72rem'}}>{fmtDate(leave.startDate)}{leave.startDate!==leave.endDate&&` → ${fmtDate(leave.endDate)}`}</div>
              <div className="date-days">{dayCount(leave.startDate,leave.endDate)} day{dayCount(leave.startDate,leave.endDate)>1?'s':''}</div>
            </div>
          </div>
          {leave.reason&&<div className="lc-row"><span className="lc-label">Reason</span><span style={{fontSize:'.74rem',color:'#6b7194',textAlign:'right',maxWidth:'60%'}}>{leave.reason}</span></div>}
          <div className="lc-row">
            <span className="lc-label">Applied</span>
            <span style={{fontSize:'.72rem',fontFamily:"'JetBrains Mono',monospace",color:'#6b7194'}}>{fmtDate(leave.appliedAt)}</span>
          </div>
          <div className="lc-actions">
            <button className="btn btn-ghost btn-sm" style={{flex:1,justifyContent:'center'}} onClick={()=>openReview(leave)}>Review</button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="lm-root">
      <style>{style}</style>

      <div className="lm-header">
        <div><div className="lm-title">Leave Management</div><div className="lm-sub">{leaves.length} applications · manage teacher leaves</div></div>
        <button className="btn btn-blue" onClick={()=>setApplyOpen(true)}><PlusIcon />Apply Leave</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="s-icon amber"><ClockIcon /></div><div className="s-label">Pending</div><div className="s-value">{pending}</div></div>
        <div className="stat-card"><div className="s-icon green"><CheckCircleIcon /></div><div className="s-label">Approved Paid</div><div className="s-value">{approvedPaid}</div></div>
        <div className="stat-card"><div className="s-icon blue"><CheckCircleIcon /></div><div className="s-label">Approved Unpaid</div><div className="s-value">{approvedUnpaid}</div></div>
        <div className="stat-card"><div className="s-icon red"><XCircleIcon /></div><div className="s-label">Rejected</div><div className="s-value">{rejected}</div></div>
      </div>

      <div className="filters">
        <FunnelIcon style={{width:16,height:16,color:'#9094a8',flexShrink:0}} />
        <select className="filter-select" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="approved-paid">Approved (Paid)</option>
          <option value="approved-unpaid">Approved (Unpaid)</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
        <select className="filter-select" value={filterTeacher} onChange={e=>setFilterTeacher(e.target.value)}>
          <option value="">All Teachers</option>
          {sortByName(teachers).map(t=><option key={t.teacherId} value={t.teacherId}>{t.name}</option>)}
        </select>
        <input type="month" className="filter-input" value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} />
        {(filterStatus||filterTeacher||filterMonth)&&(
          <button className="btn btn-ghost btn-sm" onClick={()=>{setFilterStatus('');setFilterTeacher('');setFilterMonth('');}}>Clear</button>
        )}
      </div>

      <div className="table-panel">
        {loading ? (
          <div className="lm-loading"><div className="spinner"/>Loading…</div>
        ) : leaves.length===0 ? (
          <div className="lm-empty">
            <div className="lm-empty-icon"><CalendarDaysIcon /></div>
            <div className="lm-empty-title">No leave applications</div>
            <div className="lm-empty-sub">No applications match the current filters.</div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="leave-table-wrap" style={{overflowX:'auto'}}>
              <table className="leave-table">
                <thead><tr>
                  <th>Teacher</th><th>Type</th><th>Dates</th>
                  <th>Reason</th><th>Applied</th><th>Status</th><th>Actions</th>
                </tr></thead>
                <tbody>
                  {leaves.map(leave=>(
                    <tr key={leave._id}>
                      <td><div className="teacher-cell"><div className="t-avatar">{initials(leave.teacher?.name)}</div><div><div className="t-name">{leave.teacher?.name||'—'}</div><div className="t-email">{leave.teacher?.email||''}</div></div></div></td>
                      <td><span className={`badge ${leave.type}`}>{leaveTypeLabel[leave.type]||leave.type}</span></td>
                      <td><div className="date-range">{fmtDate(leave.startDate)}{leave.startDate!==leave.endDate&&` → ${fmtDate(leave.endDate)}`}</div><div className="date-days">{dayCount(leave.startDate,leave.endDate)} day{dayCount(leave.startDate,leave.endDate)>1?'s':''}</div></td>
                      <td><div className="reason-cell" title={leave.reason}>{leave.reason||<span style={{color:'#c0c5dc'}}>—</span>}</div></td>
                      <td><div style={{fontSize:'.74rem',color:'#6b7194',fontFamily:"'JetBrains Mono',monospace"}}>{fmtDate(leave.appliedAt)}</div></td>
                      <td><span className={`badge ${leave.status}`}>{statusLabel[leave.status]}</span>{leave.reviewedBy&&<div style={{fontSize:'.67rem',color:'#b0b5cc',marginTop:3}}>by {leave.reviewedBy}</div>}</td>
                      <td><div className="action-btns"><button className="btn btn-ghost btn-sm" onClick={()=>openReview(leave)}>Review</button></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            {renderCards()}
          </>
        )}
      </div>

      {/* Apply Modal */}
      {applyOpen&&createPortal(
        <div className="modal-backdrop" onClick={()=>setApplyOpen(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Apply for Leave</div><button className="modal-close" onClick={()=>setApplyOpen(false)}><XMarkIcon /></button></div>
            <div className="form-grid">
              <div className="form-field span2">
                <label className="form-label">Teacher</label>
                <select className="form-select" value={applyForm.teacherId} onChange={e=>setApplyForm({...applyForm,teacherId:e.target.value})}>
                  <option value="">Select teacher…</option>
                  {teachers.map(t=><option key={t.teacherId} value={t.teacherId}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-field"><label className="form-label">Start Date</label><input type="date" className="form-input" value={applyForm.startDate} onChange={e=>setApplyForm({...applyForm,startDate:e.target.value})} /></div>
              <div className="form-field"><label className="form-label">End Date</label><input type="date" className="form-input" value={applyForm.endDate} min={applyForm.startDate} onChange={e=>setApplyForm({...applyForm,endDate:e.target.value})} /></div>
              <div className="form-field span2">
                <label className="form-label">Leave Type</label>
                <select className="form-select" value={applyForm.type} onChange={e=>setApplyForm({...applyForm,type:e.target.value})}>
                  <option value="sick">Sick Leave</option><option value="casual">Casual Leave</option>
                  <option value="emergency">Emergency Leave</option><option value="maternity">Maternity Leave</option>
                  <option value="unpaid">Unpaid Leave</option>
                </select>
              </div>
              <div className="form-field span2"><label className="form-label">Reason</label><textarea className="form-textarea" required value={applyForm.reason} onChange={e=>setApplyForm({...applyForm,reason:e.target.value})} placeholder="Enter reason…" /></div>
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setApplyOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleApply} disabled={submitting}>{submitting?'Saving…':'Submit Application'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Review Modal */}
      {reviewLeave&&createPortal(
        <div className="modal-backdrop" onClick={()=>setReviewLeave(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-head"><div className="modal-title">Review Leave — {reviewLeave.teacher?.name}</div><button className="modal-close" onClick={()=>setReviewLeave(null)}><XMarkIcon /></button></div>
            <div className="review-info">
              <div className="review-row"><span className="review-key">Type</span><span className="review-val">{leaveTypeLabel[reviewLeave.type]}</span></div>
              <div className="review-row"><span className="review-key">Dates</span><span className="review-val">{fmtDate(reviewLeave.startDate)}{reviewLeave.startDate!==reviewLeave.endDate&&` → ${fmtDate(reviewLeave.endDate)}`} ({dayCount(reviewLeave.startDate,reviewLeave.endDate)} days)</span></div>
              {reviewLeave.reason&&<div className="review-row"><span className="review-key">Reason</span><span className="review-val" style={{maxWidth:220,textAlign:'right'}}>{reviewLeave.reason}</span></div>}
            </div>
            <div style={{fontSize:'.72rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Decision</div>
            <div className="review-options">
              {[
                {val:'approved-paid',  label:'Approve — Paid Leave',  sub:'Teacher gets full day pay',cls:'selected-green'},
                {val:'approved-unpaid',label:'Approve — Unpaid Leave', sub:'No pay for these days',   cls:'selected-blue'},
                {val:'rejected',       label:'Reject',                 sub:'Attendance stays absent', cls:'selected-red'},
              ].map(opt=>(
                <div key={opt.val} className={`review-option ${reviewChoice===opt.val?opt.cls:''}`} onClick={()=>setReviewChoice(opt.val)}>
                  <div className={`review-option-dot ${reviewChoice===opt.val?'filled':''}`} />
                  <div><div style={{fontWeight:600}}>{opt.label}</div><div style={{fontSize:'.7rem',opacity:.7,marginTop:1}}>{opt.sub}</div></div>
                </div>
              ))}
            </div>
            <div className="form-field">
              <label className="form-label">Note to teacher (optional)</label>
              <textarea className="form-textarea" value={adminNote} onChange={e=>setAdminNote(e.target.value)} placeholder="Add a note…" style={{minHeight:55}} />
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={()=>setReviewLeave(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleReview} disabled={submitting||!reviewChoice}>{submitting?'Saving…':'Confirm Decision'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default LeaveManagement;